/* Source: /tmp/jacbuilder-deploy-scratch/deploy-8asygg26/features/graph/GraphTab.jac */
import {__jacJsx, __jacSpawn} from "@jac/runtime";
import { HugeiconsIcon } from "@hugeicons/react";
import { Share08Icon, Alert02Icon, Refresh01Icon } from "@hugeicons/core-free-icons";
import { Button } from "../../components/ui/button.js";
import { SectionHeading, EmptyPanel } from "../../components/shared/Display.js";
import { cn } from "../../lib/utils.js";
import { RecordInspector, ProvenanceBadge } from "./GraphTreePanel.js";
import { GraphCanvas } from "./GraphCanvas.js";
import { kind_title, kind_order, short_id } from "./graph_helpers.js";
import { count_of, fmt_count, is_corpus_snapshot } from "../../components/shared/Scale.js";
import { useState } from "@jac/runtime";
import { _jac } from "@jac/prelude";
function GraphTab(props) {
  const {snapshot, onRefresh} = props;
  let group;
  const [selectedId, setSelectedId] = useState("");
  const [query, setQuery] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState("");
  async function handleRefresh() {
    setRefreshing(true);
    setRefreshError("");
    try {
      await onRefresh();
    } catch (__jac_e) {
      if (_jac.exc.matches(__jac_e, "Exception")) {
        let e = __jac_e;
        setRefreshError(("Could not reload the graph: " + String(e)));
      } else {
        throw __jac_e;
      }
    }
    setRefreshing(false);
  }
  let raw = snapshot;
  let graph = ((raw && raw.graph) ? raw.graph : null);
  if ((graph == null)) {
    return __jacJsx("div", {"className": "flex flex-col gap-5"}, [__jacJsx(SectionHeading, {"title": "Graph", "subtitle": "The records behind every tab, and how they connect: source → report → person → alert."}, []), __jacJsx(EmptyPanel, {"icon": Alert02Icon, "title": "Graph projection not connected yet", "text": "The dashboard snapshot does not include a graph field yet. Once services/trace.jac sets snapshot.graph from build_graph_snapshot, records appear here."}, [])]);
  }
  let g = graph;
  let q = query.trim().toLowerCase();
  let visible = g.nodes.filter(n => ((((q === "") || n.label.toLowerCase().includes(q)) || n.kind.toLowerCase().includes(q)) || n.id.toLowerCase().includes(q))).map(n => n);
  let selected = null;
  for (const n of g.nodes) {
    if ((n.id === selectedId)) {
      selected = n;
    }
  }
  if ((selected == null)) {
    for (const n of g.nodes) {
      if (((selected == null) && (n.kind === "Claim"))) {
        selected = n;
      }
    }
  }
  let stored_count = g.edges.filter(e => !e.derived).map(e => e).length;
  let derived_count = (g.edges.length - stored_count);
  let raw_graph = g;
  let total_nodes = (raw_graph.total_nodes || g.nodes.length);
  let kind_counts = raw_graph.counts_by_kind;
  let count_keys = (kind_counts ? _jac.poly.call(kind_counts, "keys") : []);
  let summary_kinds = [...kind_order().filter(k => count_keys.includes(k)).map(k => k), ...count_keys.filter(k => !kind_order().includes(k)).map(k => k)];
  let capped = (total_nodes > g.nodes.length);
  return __jacJsx("div", {"className": "flex flex-col gap-5"}, [__jacJsx("div", {"className": "flex flex-wrap items-end justify-between gap-3"}, [__jacJsx(SectionHeading, {"title": "Graph", "subtitle": "Inspect the stored records behind every tab, their real IDs, and the typed edges between them."}, []), __jacJsx(Button, {"variant": "outline", "size": "sm", "disabled": refreshing, "onClick": handleRefresh}, [__jacJsx(HugeiconsIcon, {"icon": Refresh01Icon, "strokeWidth": 2}, []), (refreshing ? "Reloading..." : "Reload graph")])]), (refreshError && __jacJsx("div", {"role": "alert", "className": "rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive"}, [refreshError])), __jacJsx("div", {"className": "grid grid-cols-2 gap-3 sm:grid-cols-4"}, [__jacJsx("div", {"className": "rounded-md border border-border bg-card p-3"}, [__jacJsx("p", {"className": "text-xs text-muted-foreground"}, ["Records"]), __jacJsx("p", {"className": "font-display text-2xl"}, [String(g.nodes.length)])]), __jacJsx("div", {"className": "rounded-md border border-border bg-card p-3"}, [__jacJsx("p", {"className": "text-xs text-muted-foreground"}, ["Stored edges"]), __jacJsx("p", {"className": "font-display text-2xl"}, [String(stored_count)])]), __jacJsx("div", {"className": "rounded-md border border-dashed border-border bg-card p-3"}, [__jacJsx("p", {"className": "text-xs text-muted-foreground"}, ["Derived / candidate"]), __jacJsx("p", {"className": "font-display text-2xl"}, [String(derived_count)])]), __jacJsx("div", {"className": "rounded-md border border-border bg-card p-3"}, [__jacJsx("p", {"className": "text-xs text-muted-foreground"}, ["Incident jid"]), __jacJsx("p", {"className": "truncate font-mono text-sm pt-2", "title": g.incident_id}, [short_id(g.incident_id)])])]), ((is_corpus_snapshot(raw) && (summary_kinds.length > 0)) && __jacJsx("section", {"aria-label": "Records by kind", "className": "flex flex-col gap-2"}, [__jacJsx("p", {"className": "text-sm"}, [__jacJsx("span", {"className": "font-display text-2xl tabular-nums"}, [fmt_count(total_nodes)]), __jacJsx("span", {"className": "text-muted-foreground"}, [" records in this incident"])]), __jacJsx("ul", {"className": "flex flex-wrap gap-2"}, [(() => {
    let __jac_view_kids = [false];
    __jac_view_kids[0] = (() => {
      let __jac_view_kids_1 = [];
      for (const k of summary_kinds) {
        __jac_view_kids_1.push(__jacJsx("li", {"key": k, "className": "rounded-md border border-border bg-card px-2.5 py-1 text-xs"}, [__jacJsx("span", {"className": "text-muted-foreground"}, [(kind_title(k) + " ")]), __jacJsx("span", {"className": "font-medium tabular-nums"}, [fmt_count(count_of(kind_counts, k))])]));
      }
      return __jac_view_kids_1;
    })();
    return __jacJsx(null, {}, __jac_view_kids);
  })()]), (capped && __jacJsx("p", {"className": "border-l-2 border-status-warning pl-3 text-xs text-muted-foreground"}, [(((("Showing the first " + fmt_count(g.nodes.length)) + " of ") + fmt_count(total_nodes)) + " records. Search People to find others.")]))])), __jacJsx("div", {"className": "flex flex-wrap items-center gap-2 text-xs text-muted-foreground"}, [__jacJsx("span", {}, ["Legend:"]), (() => {
    let __jac_view_kids_2 = [false];
    __jac_view_kids_2[0] = (() => {
      let __jac_view_kids_3 = [];
      for (const b of ["stored", "confirmed", "candidate", "derived", "simulated", "walker ran"]) {
        __jac_view_kids_3.push(__jacJsx(ProvenanceBadge, {"key": b, "badge": b}, []));
      }
      return __jac_view_kids_3;
    })();
    return __jacJsx(null, {}, __jac_view_kids_2);
  })()]), (() => {
    let __jac_view_kids_4 = [false];
    if ((g.nodes.length === 0)) {
      __jac_view_kids_4[0] = __jacJsx(EmptyPanel, {"icon": Share08Icon, "title": "No records in this incident", "text": "Seed or reset the demo to add reports, people and media."}, []);
      return __jacJsx(null, {}, __jac_view_kids_4);
    }
    return __jacJsx(null, {}, __jac_view_kids_4);
  })(), __jacJsx(GraphCanvas, {"graph": g, "selectedId": (selected ? selected.id : ""), "onSelect": rid => {
    setSelectedId(rid);
  }}, []), __jacJsx("div", {"className": "grid items-start gap-5 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]"}, [__jacJsx("nav", {"aria-label": "Graph records", "className": "flex flex-col gap-3 rounded-lg border border-border bg-card p-3"}, [__jacJsx("label", {"className": "flex flex-col gap-1.5 text-sm"}, [__jacJsx("span", {"className": "font-medium"}, ["Find a record"]), __jacJsx("input", {"type": "search", "value": query, "onChange": e => {
    setQuery(e.target.value);
  }, "placeholder": "Name, type or jid", "className": "h-10 w-full rounded-md border border-input bg-background px-3 text-base outline-none focus-visible:ring-2 focus-visible:ring-ring"}, [])]), __jacJsx("div", {"className": "flex max-h-[28rem] flex-col gap-3 overflow-y-auto lg:max-h-[70svh]"}, [(() => {
    let __jac_view_kids_5 = [false];
    if ((visible.length === 0)) {
      __jac_view_kids_5[0] = __jacJsx("p", {"className": "p-2 text-sm text-muted-foreground"}, ["No records match."]);
      return __jacJsx(null, {}, __jac_view_kids_5);
    }
    return __jacJsx(null, {}, __jac_view_kids_5);
  })(), (() => {
    let __jac_view_kids_6 = [false];
    __jac_view_kids_6[0] = (() => {
      let __jac_view_kids_7 = [];
      for (const kind of kind_order()) {
        group = visible.filter(n => (n.kind === kind)).map(n => n);
        if ((group.length > 0)) {
          __jac_view_kids_7.push(__jacJsx("section", {"key": kind, "className": "flex flex-col gap-1"}, [__jacJsx("h3", {"className": "px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground"}, [(((kind_title(kind) + " (") + String(group.length)) + ")")]), __jacJsx("ul", {"className": "flex flex-col gap-1"}, [(() => {
            let __jac_view_kids_8 = [false];
            __jac_view_kids_8[0] = (() => {
              let __jac_view_kids_9 = [];
              for (const n of group) {
                __jac_view_kids_9.push(__jacJsx("li", {"key": n.id}, [__jacJsx("button", {"type": "button", "onClick": () => {
                  setSelectedId(n.id);
                }, "aria-current": ((selected && (selected.id === n.id)) ? "true" : "false"), "className": cn("flex w-full flex-col items-start gap-0.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring", ((selected && (selected.id === n.id)) ? "bg-primary/15 text-foreground" : "hover:bg-accent"))}, [__jacJsx("span", {"className": "line-clamp-2 break-words"}, [n.label]), __jacJsx("span", {"className": "flex flex-wrap items-center gap-1.5 font-mono text-[11px] text-muted-foreground"}, [short_id(n.id), (n.badges.includes("simulated") && __jacJsx("span", {"className": "text-status-warning"}, ["simulated"]))])])]));
              }
              return __jac_view_kids_9;
            })();
            return __jacJsx(null, {}, __jac_view_kids_8);
          })()])]));
        }
      }
      return __jac_view_kids_7;
    })();
    return __jacJsx(null, {}, __jac_view_kids_6);
  })()])]), __jacJsx(RecordInspector, {"graph": g, "record": selected, "onSelect": rid => {
    setSelectedId(rid);
  }}, [])])]);
}
/*jac:refresh-boundary*/;
export {GraphTab};
//# sourceMappingURL=GraphTab.js.map
